import React, { useState, useCallback, useMemo, useRef } from "react";
import {
  Stack,
  Stepper,
  Text,
  Group,
  Progress,
  Alert,
  Divider,
  Badge,
  Title,
} from "@mantine/core";
import { AppButton, AppIcon, FileUpload } from "@/components/generic";
import { EventImportList } from "../EventImportList";
import { ImportToolbar } from "../ImportToolbar";
import { ParsedEvent } from "../EventPreviewCard";
import { EventImportStats } from "@/utils/api";
import { parseICalFile } from "@/utils";

export interface EventImportWizardProps {
  /** Whether the wizard is disabled */
  disabled?: boolean;
  /** Maximum file size in bytes (default 5MB) */
  maxFileSize?: number;
  /** Height for the event list */
  listHeight?: number;
  /** Callback when events are imported - now returns import stats */
  onEventsImport?: (
    events: ParsedEvent[],
    venueId: string,
    importMode: "merge" | "replace",
  ) => Promise<EventImportStats | null>;
  /** Callback when a single event is imported - deprecated, use onEventsImport */
  onEventImport?: (event: ParsedEvent) => Promise<boolean>;
  /** Callback when the wizard is completed */
  onComplete?: (stats: EventImportStats) => void;
  /** Callback when the wizard is cancelled */
  onCancel?: () => void;
}

interface ImportResult {
  success: boolean;
  event: ParsedEvent;
  error?: string;
}

export const EventImportWizard: React.FC<EventImportWizardProps> = ({
  disabled = false,
  maxFileSize = 5 * 1024 * 1024, // 5MB
  listHeight = 500,
  onEventsImport,
  onEventImport,
  onComplete,
  onCancel,
}) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [parsedEvents, setParsedEvents] = useState<ParsedEvent[]>([]);
  const [selectedEventIds, setSelectedEventIds] = useState<Set<string>>(
    new Set(),
  );
  const [addingEventIds, setAddingEventIds] = useState<Set<string>>(new Set());

  // Import configuration
  const [importMode, setImportMode] = useState<"merge" | "replace">("merge");

  // Import state
  const [isBulkImporting, setIsBulkImporting] = useState(false);
  const [importResults, setImportResults] = useState<ImportResult[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [finalImportStats, setFinalImportStats] =
    useState<EventImportStats | null>(null);

  // Error handling
  const [error, setError] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  // Refs
  const importCancelledRef = useRef(false);

  // Step titles
  const stepTitles = [
    "Upload Calendar File",
    "Review Events",
    "Import Progress",
  ];

  // Parse iCal file
  const parseICalendarFile = useCallback(
    async (file: File): Promise<ParsedEvent[]> => {
      try {
        const text = await file.text();
        const parsedEvents: ParsedEvent[] = await parseICalFile(text);
        return parsedEvents;
      } catch (err) {
        throw new Error(
          `Failed to parse calendar file: ${
            err instanceof Error ? err.message : "Unknown error"
          }`,
        );
      }
    },
    [],
  );

  // Handle file selection
  const handleFileSelect = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const file = files[0];
      setParseError(null);
      setError(null);

      try {
        const events = await parseICalendarFile(file);
        setParsedEvents(events);
        setSelectedEventIds(new Set()); // Start with no events selected
        setCurrentStep(1); // Move to review step
      } catch (err) {
        setParseError(
          err instanceof Error ? err.message : "Failed to parse file",
        );
      }
    },
    [parseICalendarFile],
  );

  // Handle individual event selection
  const handleSelectionChange = useCallback(
    (eventId: string, selected: boolean) => {
      setSelectedEventIds((prev) => {
        const newSet = new Set(prev);
        if (selected) {
          newSet.add(eventId);
        } else {
          newSet.delete(eventId);
        }
        return newSet;
      });
    },
    [],
  );

  // Handle select all
  const handleSelectAll = useCallback(() => {
    setSelectedEventIds(new Set(parsedEvents.map((event) => event.tempId)));
  }, [parsedEvents]);

  // Handle deselect all
  const handleDeselectAll = useCallback(() => {
    setSelectedEventIds(new Set());
  }, []);

  // Handle import mode change
  const handleImportModeChange = useCallback((mode: "merge" | "replace") => {
    setImportMode(mode);
  }, []);

  // Handle individual event import (legacy support)
  const handleAddSingleEvent = useCallback(
    async (event: ParsedEvent) => {
      if (!onEventImport) return;

      setAddingEventIds((prev) => new Set(prev).add(event.tempId));

      try {
        const success = await onEventImport(event);
        if (success) {
          // Remove from parsed events or mark as imported
          setParsedEvents((prev) =>
            prev.filter((e) => e.tempId !== event.tempId),
          );
          setSelectedEventIds((prev) => {
            const newSet = new Set(prev);
            newSet.delete(event.tempId);
            return newSet;
          });
        }
      } catch (err) {
        setError(
          `Failed to import "${event.title}": ${
            err instanceof Error ? err.message : "Unknown error"
          }`,
        );
      } finally {
        setAddingEventIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(event.tempId);
          return newSet;
        });
      }
    },
    [onEventImport],
  );

  // Handle bulk import with proper API integration
  const handleBulkImport = useCallback(
    async (
      events: ParsedEvent[],
      venueId: string,
      mode: "merge" | "replace",
    ) => {
      if (!onEventsImport || events.length === 0) return;

      setIsBulkImporting(true);
      setCurrentStep(2); // Move to progress step
      setImportResults([]);
      setImportProgress(0);
      setFinalImportStats(null);
      importCancelledRef.current = false;

      try {
        // Start progress animation
        setImportProgress(10);

        // Call the import API with all events at once
        const importStats = await onEventsImport(events, venueId, mode);

        if (importCancelledRef.current) return;

        // Simulate progress completion
        setImportProgress(100);

        if (importStats) {
          setFinalImportStats(importStats);

          // Mark all events as successfully processed for UI
          const results: ImportResult[] = events.map((event) => ({
            success: true,
            event,
          }));
          setImportResults(results);

          // Complete the wizard
          onComplete?.(importStats);
        } else {
          throw new Error("Import failed - no results returned");
        }
      } catch (err) {
        if (!importCancelledRef.current) {
          setError(err instanceof Error ? err.message : "Import failed");

          // Mark all events as failed for UI
          const results: ImportResult[] = events.map((event) => ({
            success: false,
            event,
            error: err instanceof Error ? err.message : "Unknown error",
          }));
          setImportResults(results);
        }
      } finally {
        setIsBulkImporting(false);
      }
    },
    [onEventsImport, onComplete],
  );

  // Handle import selected
  const handleImportSelected = useCallback(
    (venueId: string, mode: "merge" | "replace") => {
      const eventsToImport = parsedEvents.filter((event) =>
        selectedEventIds.has(event.tempId),
      );
      handleBulkImport(eventsToImport, venueId, mode);
    },
    [parsedEvents, selectedEventIds, handleBulkImport],
  );

  // Handle import all
  const handleImportAll = useCallback(
    (venueId: string, mode: "merge" | "replace") => {
      handleBulkImport(parsedEvents, venueId, mode);
    },
    [parsedEvents, handleBulkImport],
  );

  // Handle cancel import
  const handleCancelImport = useCallback(() => {
    importCancelledRef.current = true;
    setIsBulkImporting(false);
    setImportProgress(0);
    setCurrentStep(1); // Go back to review step
  }, []);

  // Start over
  const handleStartOver = useCallback(() => {
    setCurrentStep(0);
    setParsedEvents([]);
    setSelectedEventIds(new Set());
    setAddingEventIds(new Set());
    setIsBulkImporting(false);
    setImportResults([]);
    setImportProgress(0);
    setFinalImportStats(null);
    setError(null);
    setParseError(null);
    setImportMode("merge");
    importCancelledRef.current = false;
  }, []);

  // Results summary
  const resultsSummary = useMemo(() => {
    if (finalImportStats) {
      return {
        total: finalImportStats.inserted + finalImportStats.updated,
        successful: finalImportStats.inserted + finalImportStats.updated,
        failed: 0,
        inserted: finalImportStats.inserted,
        updated: finalImportStats.updated,
      };
    }

    // Fallback to old results format
    const total = importResults.length;
    const successful = importResults.filter((r) => r.success).length;
    const failed = total - successful;

    return {
      total,
      successful,
      failed,
      inserted: successful, // Approximate
      updated: 0, // Can't distinguish with old format
    };
  }, [importResults, finalImportStats]);

  return (
    <Stack gap="lg">
      {/* Stepper */}
      <Stepper active={currentStep} allowNextStepsSelect={false} size="sm">
        {stepTitles.map((title, index) => (
          <Stepper.Step
            key={index}
            label={title}
            icon={
              index === 0 ? (
                <AppIcon iconName="Upload" size={16} />
              ) : index === 1 ? (
                <AppIcon iconName="Eye" size={16} />
              ) : (
                <AppIcon iconName="Download" size={16} />
              )
            }
          />
        ))}
      </Stepper>

      {/* Error display */}
      {(error || parseError) && (
        <Alert
          color="red"
          icon={<AppIcon iconName="AlertCircle" size={16} />}
          onClose={() => {
            setError(null);
            setParseError(null);
          }}
          withCloseButton
        >
          {error || parseError}
        </Alert>
      )}

      {/* Step 0: File Upload */}
      {currentStep === 0 && (
        <Stack gap="md">
          <FileUpload
            accept=".ics,text/calendar"
            maxSize={maxFileSize}
            onFilesSelected={handleFileSelect}
            disabled={disabled}
            placeholder="Drop your calendar file (.ics) here or click to browse"
            description="Select a calendar file to import events"
          />

          {/* Additional info */}
          <Stack gap="xs">
            <Title order={3}>Supported Formats</Title>
            <Text size="xs" c="dimmed">
              • iCalendar (.ics) files from Google Calendar, Outlook, Apple
              Calendar, and other calendar applications
            </Text>
            <Text size="xs" c="dimmed">
              • Maximum file size: {Math.round(maxFileSize / 1024 / 1024)}MB
            </Text>
            <Text size="xs" c="dimmed">
              • Events will be imported with their original dates, times, and
              descriptions
            </Text>
            <Text size="xs" c="dimmed">
              • You can review and select which events to import before
              confirming
            </Text>
          </Stack>
        </Stack>
      )}

      {/* Step 1: Review Events */}
      {currentStep === 1 && (
        <Stack gap="md">
          <ImportToolbar
            totalEvents={parsedEvents.length}
            selectedCount={selectedEventIds.size}
            importingCount={addingEventIds.size}
            bulkImporting={isBulkImporting}
            disabled={disabled}
            importMode={importMode}
            onImportModeChange={handleImportModeChange}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            onImportSelected={handleImportSelected}
            onImportAll={handleImportAll}
            onCancelImport={handleCancelImport}
          />

          <EventImportList
            events={parsedEvents}
            selectedEventIds={selectedEventIds}
            addingEventIds={addingEventIds}
            disabled={disabled || isBulkImporting}
            height={listHeight}
            onSelectionChange={handleSelectionChange}
            onAddEvent={handleAddSingleEvent}
          />

          <Group justify="space-between">
            <AppButton variant="secondary" onClick={handleStartOver}>
              Start Over
            </AppButton>
            {onCancel && (
              <AppButton variant="secondary" onClick={onCancel}>
                Cancel
              </AppButton>
            )}
          </Group>
        </Stack>
      )}

      {/* Step 2: Import Progress */}
      {currentStep === 2 && (
        <Stack gap="md">
          {isBulkImporting ? (
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text size="lg" fw={600}>
                  Importing Events...
                </Text>
                <Text size="sm" c="dimmed">
                  {importProgress}% complete
                </Text>
              </Group>

              <Progress value={importProgress} size="lg" striped animated />

              <Text size="sm" c="dimmed" ta="center">
                Processing{" "}
                {selectedEventIds.size > 0
                  ? selectedEventIds.size
                  : parsedEvents.length}{" "}
                events...
              </Text>

              <Group justify="center">
                <AppButton variant="danger" onClick={handleCancelImport}>
                  Cancel Import
                </AppButton>
              </Group>
            </Stack>
          ) : (
            <Stack gap="md">
              <Group justify="space-between" align="center">
                <Text size="lg" fw={600}>
                  Import Complete
                </Text>
                <Group gap="xs">
                  {resultsSummary.successful > 0 && (
                    <Badge color="green" size="lg">
                      {resultsSummary.successful} total
                    </Badge>
                  )}
                  {resultsSummary.inserted > 0 && (
                    <Badge color="blue" size="lg">
                      {resultsSummary.inserted} new
                    </Badge>
                  )}
                  {resultsSummary.updated > 0 && (
                    <Badge color="orange" size="lg">
                      {resultsSummary.updated} updated
                    </Badge>
                  )}
                  {resultsSummary.failed > 0 && (
                    <Badge color="red" size="lg">
                      {resultsSummary.failed} failed
                    </Badge>
                  )}
                </Group>
              </Group>

              {finalImportStats && (
                <Alert
                  color="green"
                  icon={<AppIcon iconName="CircleCheck" size={16} />}
                >
                  <Text size="sm">
                    Successfully processed {resultsSummary.total} events.
                    {finalImportStats.inserted > 0 && (
                      <> {finalImportStats.inserted} new events were created</>
                    )}
                    {finalImportStats.updated > 0 && (
                      <>
                        {finalImportStats.inserted > 0 ? " and " : " "}
                        {finalImportStats.updated} existing events were updated
                      </>
                    )}
                    .
                  </Text>
                </Alert>
              )}

              {resultsSummary.failed > 0 && (
                <Alert
                  color="orange"
                  icon={<AppIcon iconName="AlertTriangle" size={16} />}
                >
                  <Text size="sm">
                    {resultsSummary.failed} events could not be imported. Check
                    the error messages above.
                  </Text>
                </Alert>
              )}

              <Divider />

              <Group justify="space-between">
                <AppButton variant="secondary" onClick={handleStartOver}>
                  Import More Events
                </AppButton>
                <Group gap="sm">
                  {onCancel && (
                    <AppButton variant="secondary" onClick={onCancel}>
                      Close
                    </AppButton>
                  )}
                </Group>
              </Group>
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
};
